// libraries
import {uniqueId, isNil} from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';

// shared
import Icon from './icon';

import {PERIOD, DASH} from 'shared/keys';

import 'stylesheets/components/shared-text-input';
import 'stylesheets/components/shared-error-icon';

class TextInput extends React.Component {
  static propTypes = {
    id: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string]),
    min: PropTypes.number,
    max: PropTypes.number,
    active: PropTypes.bool,
    name: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    invalid: PropTypes.bool,
    touched: PropTypes.bool,
    onFocus: PropTypes.func,
    valid: PropTypes.bool,
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string
    ]),
    placeholder: PropTypes.string.isRequired,
    focusOnMount: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string
    ]),
    debounce: PropTypes.number,
    onKeyDown: PropTypes.func,
    onInputChange: PropTypes.func,
    onInputBlur: PropTypes.func,
    onInputFocus: PropTypes.func,
    selectValueOnFocus: PropTypes.bool,
    type: PropTypes.string,
    children: PropTypes.element,
    disabled: PropTypes.bool,
    showCheck: PropTypes.bool,
    required: PropTypes.bool,
    step: PropTypes.number,
    autoComplete: PropTypes.bool,
    autoCompleteType: PropTypes.string,
    'aria-label': PropTypes.string,
    'aria-labelledby': PropTypes.string,
    'aria-describedby': PropTypes.string,
    'aria-required': PropTypes.bool,
    forceError: PropTypes.bool,
    size: PropTypes.oneOf([
      'small', 'medium', 'large'
    ])
  };

  static defaultProps = {
    focusOnMount: false,
    selectValueOnFocus: false,
    disabled: false,
    type: 'text',
    debounce: 0,
    onInputChange: () => {},
    onFocus: () => {},
    required: false,
    step: 1,
    autoComplete: true,
    autoCompleteType: null,
    forceError: false,
    size: 'medium'
  };

  UNSAFE_componentWillMount() {
    this.timeout = null;
    this.selfId = (this.props.id)
      ? this.props.id
      : uniqueId('shared-text-input');
  }

  componentDidMount() {
    if (this.props.focusOnMount) {
      this.focus();
    }
  }

  debounceChange = (event) => {
    const {onInputChange, debounce, onChange} = this.props;
    event.persist();
    onChange(event);

    const later = () => {
      this.timeout = null;
      onInputChange(event);
    };

    clearTimeout(this.timeout);
    this.timeout = setTimeout(later, debounce);
  };

  focus = (e) => {
    const {
      onFocus,
      onInputFocus,
      selectValueOnFocus
    } = this.props;

    selectValueOnFocus ? this.input.select() : this.input.focus();

    onFocus(e);
    if (onInputFocus) {
      onInputFocus();
    }
  };

  // extra validations for Safari which doesn't respect type number
  handleKeyDown(e) {
    const {step, onKeyDown, type, min} = this.props;

    if (onKeyDown) {
      onKeyDown(e);
    }

    if (type === 'number') {
      // disallow alpha chars unless command key is pressed
      if ((e.which >= 65 && e.which <= 90) && (!e.metaKey)) {
        e.preventDefault();
      }

      // disallow symbols with shift + digit key pattern
      if (e.shiftKey && (e.which > 47 && e.which < 58)) {
        e.preventDefault();
      }

      // don't allow symbols except for negative numbers
      if ((e.which >= 186 && e.which <= 222) && (e.which !== PERIOD && e.which !== DASH)) {
        e.preventDefault();
      }

      // don't allow dash for positive numbers
      if (e.which === DASH && min > 0) {
        e.preventDefault();
      }

      // allow periods based on step's integer status
      if (step % 1 === 0 && e.which === PERIOD) {
        e.preventDefault();
      }
    }
  }

  render() {
    const {
      id,
      invalid,
      touched,
      active,
      valid,
      value,
      min,
      max,
      onBlur,
      error,
      disabled,
      name,
      onKeyDown,
      onInputBlur,
      onChange,
      type,
      debounce,
      children,
      onInputChange,
      required,
      step,
      autoComplete,
      autoCompleteType,
      forceError,
      placeholder,
      size
    } = this.props;

    const showError = (invalid && touched && !active && (required || !!value)) || forceError;
    const showCheck = this.props.showCheck && valid && !active;
    const number = type === 'number';

    const ariaProps = {};
    if (this.props['aria-label']) {
      ariaProps['aria-label'] = this.props['aria-label'];
    }
    if (this.props['aria-labelledby']) {
      ariaProps['aria-labelledby'] = this.props['aria-labelledby'];
    }
    // Describe allows you to link extra text to a form field that is read after
    // the label (for instance, a password field/labelby followed by rules on
    // how many characters are at a minimum)
    if (this.props['aria-describedby']) {
      ariaProps['aria-describedby'] = this.props['aria-describedby'];
    }
    if (showError) {
      // the below will handle the case where multiple textbox errors exist
      ariaProps['aria-describedby'] = (typeof ariaProps['aria-describedby'] === 'undefined')
        ? `${this.selfId}-error`
        : `${ariaProps['aria-describedby']} ${this.selfId}-error`;
    }
    // NOTE: aria-disabled and aria-required are no longer needed as disabled and
    // required are handled identically. Just in case (because we are handling
    // required in react), I added support for aria-required (but not disabled)
    if (this.props['aria-required']) {
      ariaProps['aria-required'] = this.props['aria-required'];
    } else if (required) {
      // 97% of a17y browsers will announce on `required` only, but lets add
      // `aria-required` just in case for some reason we forget to put `required`
      // in the form element ;)
      ariaProps['aria-required'] = true;
    }
    if (showError) {
      ariaProps['aria-invalid'] = true;
    }

    return (
      <div className="shared-text-input">
        <input
          id={this.selfId}
          className={classnames('hidden-ms-input shared-text-input-input', size, {disabled}, {error: showError}, {number})}
          ref={(c) => { this.input = c; }}
          placeholder={placeholder}
          onKeyDown={(e)=>{ this.handleKeyDown(e); }}
          onChange={debounce > 0 ? this.debounceChange : (e) => {
            onChange(e);
            onInputChange(e);
          }}
          onBlur={onInputBlur || onBlur}
          type={type}
          disabled={disabled}
          value={isNil(value) ? '' : value}
          min={min}
          max={max}
          name={name}
          onFocus={this.focus}
          step={step}
          autoComplete={autoComplete ? autoCompleteType : 'off'}
          {...{required}}
          {...ariaProps}
        />
        {showCheck &&
          <Icon className={classnames('shared-text-input-checkmark', size)} iconType="check" />
        }
        {children}
        {showError &&
          <React.Fragment>
            <Icon className={classnames('shared-error-icon', size)} iconType="exclamation-circle-r" />
            <div id={`${this.selfId}-error`} className="shared-error-text" role="alert">
              {error}
            </div>
          </React.Fragment>
        }
      </div>
    );
  }
}

export default TextInput;
